<template>
    <div
        class="filterColumn"
        :class="{
            'is-disabled': disabled,
        }"
    >
        <div
            class="button filterColumn__button"
            :class="filterClasses"
            @click="handleClick($event)"
        >
            <img :src="`${$window.Config.cdn_url}/images/app/svg/table-columns.svg`" alt="" />
        </div>

        <div v-if="show" class="filterColumn__container">
            <div class="filterColumn__search">
                <form-input
                    v-model="form.search"
                    class="filterColumn__search__input"
                    placeholder="Search..."
                    autocomplete="off"
                    :focus="true"
                    :error="form.errors.get('search')"
                />
                <button
                    v-if="searchList"
                    class="filterColumn__search__clear"
                    @click.prevent="clear"
                >
                    <fa-icon icon="times" />
                </button>
            </div>
            <ul v-if="isShowSearchResult" class="filterColumn__search__result">
                <li v-for="item in searchList" :key="item.id">
                    <div
                        v-tooltip.top="item.label.length > 13 && item.label"
                        class="filterColumn__item"
                    >
                        <checkbox
                            :item="item"
                            :label="item.label"
                            :value="item.value"
                            :disabled="item.disabled"
                            :limitValue="13"
                            @handle-change="handleChange"
                        />
                    </div>
                </li>
            </ul>
            <template v-else>
                <ul class="filterColumn__group">
                    <template v-for="elem in groups">
                        <li v-if="elem.show" :key="elem.id">
                            <div class="filterColumn__group__header">
                                {{ elem.name }}
                            </div>
                            <template v-for="item in list">
                                <div
                                    v-if="elem.key === item.class"
                                    :key="item.id"
                                    v-tooltip.top="item.label.length > 13 && item.label"
                                    class="filterColumn__item"
                                >
                                    <checkbox
                                        :item="item"
                                        :label="item.label"
                                        :value="item.value"
                                        :disabled="item.disabled"
                                        :limitValue="13"
                                        @handle-change="handleChange"
                                    />
                                </div>
                            </template>
                        </li>
                    </template>
                </ul>
            </template>
        </div>
    </div>
</template>

<script>
import { mapState } from 'pinia';
import stores from 'store/modules';
import { debounce } from 'lodash';
import Form from 'core/Form';

export default {
    props: {
        contactsFilters: { type: Object, default: null },
        disabled: { type: Boolean, default: false },
    },

    data() {
        return {
            show: false,
            list: [],
            groups: null,
            form: new Form({
                search: '',
            }),
            isShowSearchResult: false,
        };
    },

    computed: {
        filterClasses() {
            return {
                filter__show: this.show,
            };
        },

        checkedList() {
            return this.list.filter((item) => item.value);
        },

        checkedListParams() {
            return this.checkedList.map((item) => item.key);
        },

        hasActiveFields() {
            return this.contactActiveFieldsColumn !== null;
        },

        searchList() {
            if (this.form.search) {
                return this.list.filter((item) => {
                    return item.label.toLowerCase().includes(this.form.search.toLowerCase());
                });
            }

            return null;
        },

        ...mapState(stores.Organization, ['customFieldsList']),
        ...mapState(stores.User, ['contactActiveFieldsColumn', 'userPermissions']),
    },

    watch: {
        'form.search'() {
            this.isShowSearchResult = this.form.search.length > 0;
        },

        contactsFilters() {
            if (this.contactsFilters) this.init();
        },
    },

    methods: {
        async init() {
            if (this.contactsFilters) {
                await this.initGroups();
                await this.initList();
            }

            if (!this.hasActiveFields) {
                await this.handleUpdateList();
            }
        },

        initGroups() {
            const initData = [
                'contact_fields',
                'custom_fields',
                'geo-location',
                'activity',
                'phone-checker',
            ];

            this.groups = initData.map((item, index) => ({
                id: index,
                key: item,
                name: item.replace('_', ' '),
                show: !(
                    (this.customFieldsList.length < 1 && item === 'custom_fields') ||
                    (!this.hasHubSpotIntegration && item === 'integrations')
                ),
                data: [],
            }));
        },

        initList() {
            const customFields = this.customFieldsList.map((item) => ({
                class: 'custom_fields',
                key: item.key,
                label: item.name,
                value: false,
                disabled: false,
            }));

            const filteredData = this.contactsFilters.filters.filter((item) => {
                if (
                    item.key !== 'first_name' &&
                    item.key !== 'last_name' &&
                    item.key !== 'opt_in'
                ) {
                    return item;
                }
            });

            const createFields = filteredData.map((item) => {
                if (item.category) {
                    return {
                        class: item.category,
                        key: item.key,
                        label: item.name,
                        value: false,
                        disabled: false,
                    };
                }

                return {
                    class: 'contact_fields',
                    key: item.key,
                    label: item.name,
                    value: false,
                    disabled: item.key === 'number',
                };
            });

            let integrations = this.hasHubSpotIntegration
                ? [
                      {
                          class: 'integrations',
                          key: 'hubspot',
                          label: 'HubSpot Lists',
                          value: false,
                          disabled: false,
                      },
                  ]
                : [];

            this.list = [
                {
                    class: 'contact_fields',
                    key: 'name',
                    label: 'Name',
                    value: true,
                    disabled: true,
                },
                ...createFields,
                ...customFields,
                ...integrations,
            ];

            if (this.hasActiveFields) {
                this.list = this.list.map((item) => {
                    item.value = this.contactActiveFieldsColumn.includes(item.key);

                    return item;
                });
            }

            this.handleGetValueForKeys();
        },

        handleChange() {
            this.debounceHandleUpdateList();
        },

        handleClick() {
            this.show = !this.show;
        },

        handleOutSideHide(e) {
            if (!this.$el.contains(e.target) && e.target.tagName !== 'BODY') {
                this.show = false;
            }
        },

        async handleUpdateList() {
            await this.$store.dispatch('updateContactsFields', {
                contact_fields: this.checkedListParams,
            });
            this.handleGetValueForKeys();
        },

        handleGetValueForKeys() {
            const allKeys = [
                'area_code',
                'state_code',
                'time_zone',
                'last_called_on',
                'last_messaged_on',
                'last_contacted_on',
                'last_message_sent_on',
                'last_message_received_on',
                'message_status',
                'opt_in',
                'total_received_sms',
                'total_received_call',
            ];
            let values = [];
            this.checkedListParams.forEach((item) => {
                allKeys.forEach((key) => {
                    if (key === item) {
                        values.push(item);
                    }
                });
            });

            if (values.length) this.$store.dispatch('contactsUpdateColumns', { columns: values });
        },

        clear() {
            this.form.search = '';
            this.isShowSearchResult = false;
        },
    },

    mounted() {
        this.init();

        this.debounceHandleUpdateList = debounce(this.handleUpdateList, 1000);

        document.addEventListener('click', this.handleOutSideHide);
    },

    destroy() {
        document.removeEventListener('click', this.handleOutSideHide);
    },
};
</script>
